import {useState, useMemo} from "react"
import {useSessionContext} from "../../reactContext/contextCustomHooks/useSessionContext"
import {useNwDashboardContext} from "../../reactContext/contextCustomHooks/useNwDashboardContext"
import {useTranslation} from "react-i18next"
import {useForm} from "react-hook-form"
import useUser from "../../swrCustomHooks/useUser"
import axios from "axios"
import {API_TEAMS} from "../../constants/apiUrls"
import Select from "react-select"
import LoadingComponent from "../../components/loading-component/LoadingComponent"
import {toast} from "react-toastify"
import {labelMap} from "../../constants/multilingual"

export default function UpdateTeamForm({team, mutateTeam, mutateTeamOverview}) {
    const {t} = useTranslation()
    const {sessionContext} = useSessionContext()
    const {setNwDashboardAction} = useNwDashboardContext()
    const {user, isLoadingUser, isErrorUser} = useUser(team.team.id_admin)
    const [selectValue, setSelectValue] = useState(team.team.id_admin)
    const [licenceTypes, setLicenceTypes] = useState(team.team.licence)
    const [languages, setLanguages] = useState(team.team.languages)
    const [isLanguages, setIsLanguages] = useState(true)
    const {register, handleSubmit, formState: {errors}} = useForm({defaultValues: {
        name: team.team.name,
        maxUsers: team.team.max_users,
        nbCredits: team.team.nb_credits,
        nbCreditsEcp: team.team.nb_credits_ecp,
        maxTranscription: team.team.max_transcription_per_project,
        maxTraslation: team.team.max_translation_per_project,
        maxDuration: team.team.max_duration,
        licenceStartDate: team.team.licence_start_date.split("T")[0],
        licenceEndDate: team.team.licence_end_date.split("T")[0],
        urlLabel: team.team.licence_package.url_label,
        url: team.team.licence_package.url,
    }})

    const licenceTypesOptions = [{value: "st", label: "st"}, {value: "ecp", label: "ecp"}]
    const teamManagerOptions = []
    try {
        for (let teamUser of team.users) {
            teamManagerOptions.push({value: teamUser.id, label: teamUser.email})
        }
    } catch {}

    const transformLabelMap = (labelMap) => {
        return Object.entries(labelMap).map(([code, label]) => ({
            value: code,
            label: t(label),
        })).sort((a, b) => a.label.localeCompare(b.label))
    }

    const transformLicenceTypes = useMemo(() => {
        return licenceTypes
            .map((type) => ({
                value: type,
                label: type,
            }))
    }, [licenceTypes])

    const transformLanguages = useMemo(() => {
        if (languages.length === 0) return []
        if (languages[0].code) {
            return languages
                .map((lang) => ({
                    value: lang.code,
                    label: t(labelMap[lang.code]),
                }))
                .sort((a, b) => a.label.localeCompare(b.label))
        } else {
            try {
                return languages.sort((a, b) => a.label.localeCompare(b.label))
            } catch {}
        }
    }, [languages, labelMap])

    const handleRequest = async (data) => {
        if (languages.length > 1) {
            setIsLanguages(true)
            let languagesValues = []
            let licenceTypesValues = []
            for (let element of languages) {
                languagesValues.push(element.value || element.code)
            }
            for (let element of licenceTypes) {
                licenceTypesValues.push(element?.value || element)
            }
            const bodyRequest = {
                name: data.name,
                licence_start_date: data.licenceStartDate,
                licence_end_date: data.licenceEndDate,
                licence_package: {url_label: data.urlLabel || "", url: data.url || ""},
                max_users: parseInt(data.maxUsers),
                nb_credits: parseInt(data.nbCredits),
                nb_credits_ecp: parseInt(data.nbCreditsEcp),
                max_transcription_per_project: parseInt(data.maxTranscription),
                max_translation_per_project: parseInt(data.maxTraslation),
                max_duration: parseInt(data.maxDuration),
                languages: languagesValues,
                id_admin: selectValue.value,
                licence: licenceTypesValues,
            }
            const headersRequest = {headers: {Authorization: `Bearer ${sessionContext.idToken}`}}
            axios.put(`${API_TEAMS}/${team.team.id}`, bodyRequest, headersRequest)
            .then(() => {
                mutateTeam()
                mutateTeamOverview()
                setNwDashboardAction({type: "NW_DASHBOARD_IS_MODAL", payload: {isModal: false}})
                toast.success(t("Changes made"))
            }).catch((error) => {
                console.error("PUT /teams/team: ", error)
            })
        } else {
            setIsLanguages(languages.length <= 1 ? false : true)
        }
    }

    if (isErrorUser || isLoadingUser || !user) return <LoadingComponent/>
    return (
        <>
        <form className="update-team-form" onSubmit={handleSubmit(handleRequest)}>
            <div className="category">
                <h3>{t("LICENCE")}</h3>
                <div className="field">
                    <label for="">{t("Licence name")}*</label>
                    <input type="text" id="name" name="name" placeholder={t("Licence name")} {...register("name", {required: true, minLength: 2})}/>
                </div>
                <div className="field">
                    <label for="">{t("Number of creators")}*</label>
                    <input type="number" id="max-users" name="max-users" placeholder={t("Ex. Project 01")} min="1" {...register("maxUsers", {required: true})}/>
                </div>
                <div className="field">
                    <label for="">{t("Start")}*</label>
                    <input type="date" id="licence-start-date" name="licence-start-date" placeholder="" {...register("licenceStartDate", {required: true})}/>
                </div>
                <div className="field">
                    <label for="">{t("End")}*</label>
                    <input type="date" id="licence-end-date" name="licence-end-date" placeholder="" {...register("licenceEndDate", {required: true})}/>
                </div>
                <label for="">Type*</label>
                <Select
                    defaultValue={transformLicenceTypes}
                    placeholder={t("Select licence type")}
                    isMulti
                    ame="licence-type"
                    options={licenceTypesOptions}
                    closeMenuOnSelect={false}
                    isClearable={false}
                    isSearchable={false}
                    className={`basic-multi-select ${!licenceTypes && "select-not-valid"}`}
                    classNamePrefix="select"
                    onChange={(value) => setLicenceTypes(value)}
                />
                <label for="">Team manager*</label>
                <Select
                    defaultValue={{value: user.id, label: user.email}}
                    placeholder={t("Select Team manager")}
                    isClearable={false}
                    isSearchable={false}
                    menuShouldScrollIntoView={false}
                    maxMenuHeight={110}
                    className="basic-single"
                    classNamePrefix="select"
                    options={teamManagerOptions}
                    onChange={(value) => setSelectValue(value)}
                />
            </div>
            <div className="category">
                <h3>{t("CHARACTERISTICS")}</h3>
                <div className="field">
                    <label for="">{t("Credits ST")}*</label>
                    <input type="number" id="nb-credits" name="nb-credits" placeholder="Ex. 10" min="0" {...register("nbCredits", {required: true})}/>
                </div>
                <div className="field">
                    <label for="">{t("Credits ECP")}*</label>
                    <input type="number" id="nb-credits-ecp" name="nb-credits-ecp" placeholder="Ex. 10" min="0" {...register("nbCreditsEcp", {required: true})}/>
                </div>
                <div className="field">
                    <label for="">{t("Max audio / video duration")}*</label>
                    <input type="number" id="max-duration" name="max-duration" placeholder="Ex. 15 min" min="1" {...register("maxDuration", {required: true})}/>
                </div>
                <div className="field">
                    <label for="">{t("Number of transcriptions allowed")}*</label>
                    <input type="number" id="max-transcription" name="max-transcription" placeholder="Ex. 03" min="1" {...register("maxTranscription", {required: true})}/>
                </div>
                <div className="field">
                    <label for="">{t("Number of translations allowed")}*</label>
                    <input type="number" id="max-traslation" name="max-traslation" placeholder="Ex. 03" min="1" {...register("maxTraslation", {required: true})}/>
                </div>
                <label>{`${t("Team Languages")}*`}</label>
                <Select
                    value={transformLanguages}
                    placeholder={t("Select languages")}
                    isMulti
                    name="team-languages"
                    options={transformLabelMap(labelMap)}
                    closeMenuOnSelect={false}
                    isClearable={false}
                    isSearchable={false}
                    className={`basic-multi-select ${!isLanguages && "select-not-valid"}`}
                    classNamePrefix="select"
                    onChange={(value) => setLanguages(value || [])}
                />
                <h3>{t("package")}</h3>
                <div className="field">
                    <label for="">{t("Url label")}</label>
                    <input  className={`${errors.urlLabel ? "not-valid" : ""}`} type="text" id="urlLabel" name="urlLabel" placeholder="Ex. Nolej"  {...register("urlLabel", {minLength: 2})}/>
                </div>
                <div className="field">
                    <label for="">{t("Url")}</label>
                    <input className={`${errors.url ? "not-valid" : ""}`} type="text" id="url" name="url" placeholder="Ex. https://..." {...register("url", {pattern: /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/})}/>
                </div>
            </div>
            <button>{t("Update")}</button>
        </form>
        </>
    )
}